import decodeJwt from "jwt-decode";
import { reset } from "./services/MixpanelService";
import { setAuthData, validateAuthorization } from "./utils/AuthUtils";
import { canRoleAccessResource } from "./utils/AccessControl";
import { ROLE_TYPES } from "./enums/Roles";
import ERROR_CODES from "./common/ErrorCodes";

const { REACT_APP_BASE_URL } = process.env;

const ALLOWED_BACKOFFICE_ROLES = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.RESTAURANT,
  ROLE_TYPES.RESTAURANT_DRAFT,
];

const authProvider = {
  login: ({ username, password, navigate }) => {
    const request = new Request(`${REACT_APP_BASE_URL}/auth`, {
      method: "POST",
      body: JSON.stringify({ email: username, password }),
      headers: new Headers({ "Content-Type": "application/json" }),
    });
    return fetch(request)
      .then(async (response) => {
        const data = await response.json();
        if (response.status === 401) {
          if (data?.message === ERROR_CODES?.ACCOUNT_NOT_CONFIRMED) {
            throw new Error(ERROR_CODES?.ACCOUNT_NOT_CONFIRMED);
          }
          throw new Error(ERROR_CODES?.UNAUTHORIZED);
        }
        return data;
      })
      .then(async (response) => {
        if (!ALLOWED_BACKOFFICE_ROLES.includes(response.role)) {
          throw new Error(ERROR_CODES.ROLE_NOT_ALLOWED);
        }
        const { token } = response;
        // Customers use the mobile app, not the back office. Reject before storing
        // any auth data so no session is created for them.
        if (decodeJwt(token)?.role === ROLE_TYPES.CUSTOMER) {
          throw new Error(ERROR_CODES?.UNAUTHORIZED);
        }
        await setAuthData(token);
        return response;
      });
  },
  register: ({ email, name, phone, instagram }) => {
    const request = new Request(
      `${REACT_APP_BASE_URL}/auth/register/restaurant/draft`,
      {
        method: "POST",
        headers: new Headers({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          email,
          name,
          phone,
          instagram,
          password: email,
        }),
      }
    );

    return fetch(request)
      .then(async (response) => response.json())

      .catch((error) => {
        console.error("Registration failed:", error);
        throw new Error(`Registration failed: ${error.message}`);
      });
  },
  // called when the user clicks on the logout button
  logout: () => {
    localStorage.clear();
    reset();
    return Promise.resolve();
  },
  // called when the API returns an error
  checkError: (err) => {
    validateAuthorization(err);
    return Promise.resolve();
  },
  // called when the user navigates to a new location, to check for authentication
  checkAuth: () => {
    if (!localStorage.getItem("token")) {
      return Promise.reject({ message: false });
    }
    // Customers have no business in the back office. Covered here as well as in
    // login() so an already-stored session cannot browse it either.
    if (localStorage.getItem("role") === ROLE_TYPES.CUSTOMER) {
      localStorage.clear();
      return Promise.reject({ message: false });
    }
    return Promise.resolve();
  },
  // called when the user navigates to a new location, to check for permissions / roles
  getPermissions: () => {
    const role = localStorage.getItem("role");
    return Promise.resolve(role);
  },
  // Page-level authorization. react-admin calls this on every list/show/edit/create
  // (useListController -> useRequireAccess) and to build the menu, and it redirects
  // to /access-denied on false — so the rules live in one place and default to
  // allow. See utils/AccessControl.js.
  canAccess: ({ resource, action }) => {
    const role = localStorage.getItem("role");
    return Promise.resolve(canRoleAccessResource({ resource, action, role }));
  },
  getIdentity: () => {
    try {
      const user = localStorage.getItem("user");
      if (!user) return Promise.reject();
      const { id, firstName, lastName } = JSON.parse(
        localStorage.getItem("user")
      );
      return Promise.resolve({
        id,
        fullName: `${firstName} ${lastName}`,
      });
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

export default authProvider;
